import { useCallback, useMemo, useState } from "react";
import appStyles from "../../../App.module.css";
import { formatAmount } from "../../../app-constants";
import { benefitSummaryTableEU, benefitSummaryTableUK } from "../../config/bt-constants";

const BenefitSummaryTableModal = ({ version, quoteData }: any) => {
  const [expandIndex, setExpandIndex] = useState<number | null>(null);
  const { host_country, currency } = quoteData || {};
  const countryCode = host_country?.code || version;
  const currencyCode = currency || (version === "UK" ? "GBP" : "EUR");

  const benefitList = useMemo(() => {
    return version === "EU" ? benefitSummaryTableEU : benefitSummaryTableUK;
  }, [version]);

  const formatLegalExpenses = useCallback(
    (benefit: string) => {
      const [part1, part2]: any = benefit.split("/");
      const formattedPart1 = formatAmount(part1.trim(), "US", currencyCode, 0);
      const formattedPart2 = formatAmount(part2.trim(), "US", currencyCode, 0);
      return `${formattedPart1} / ${formattedPart2}`;
    },
    [countryCode, currencyCode]
  );

  const formattedValue = useCallback(
    (benefits: any) => {
      const { name, benefit } = benefits;
      if (name === "Legal Expenses / Personal Liability") {
        return formatLegalExpenses(benefit);
      } else {
        const number = benefit.match(/\d+/)?.[0];
        if (number) {
          const formattedNumber = formatAmount(number, "US", currencyCode, 0);
          return benefit.replace(number, formattedNumber);
        }
        return benefit;
      }
    },
    [countryCode, currencyCode, formatLegalExpenses]
  );

  return (
    <div className={appStyles.btBenefitWrapper}>
      <h2 className={appStyles.btBenefitSummaryTitle}>Policy benefits</h2>
      <p className={appStyles.btBenefitSummaryDescription}>
        Underwritten by certain underwriters at Lloyd's and provided by battleface Insurance Services Ltd.
      </p>
      <div className={appStyles.btAccordionWrapper}>
        {benefitList?.map((benefits: any, index: number) => (
          <div className={appStyles.btAccordionRow} key={index}>
            <div
              className={appStyles.btAccordionHeadingWrap}
              onClick={() => setExpandIndex(expandIndex === index ? null : index)}
            >
              <div>
                <div className={appStyles.btBenefitHeading}>{benefits?.name}</div>
                <div className={appStyles.btBenefitPrice}>{formattedValue(benefits)}</div>
              </div>
              <div>
                <span className={expandIndex === index ? appStyles.btAccordionIcon : appStyles.btAccordionOpen}></span>
              </div>
            </div>
            {expandIndex === index && <div className={appStyles.btBenefitDescription}>{benefits.description}</div>}
          </div>
        ))}
      </div>

      <p className={appStyles.btBenefitSummaryNote}>
        * The benefit must not exceed 100% of an insured person’s gross weekly wage.
      </p>
      <p className={appStyles.btBenefitSummaryNote}>
        ** The benefit must not exceed 50% of an insured person’s gross weekly wage.
      </p>
      <p className={appStyles.btBenefitSummaryNote}>
        Note: Benefits offered are per trip per insured (unless stated otherwise). Terms, Conditions, Exclusions and
        Limitations apply. Please read the full policy wording to check that it is suitable for your needs.
      </p>
    </div>
  );
};

export default BenefitSummaryTableModal;
